import React from "react";
import {
    DataGrid,
    GridColDef,
    GridRowsProp,
    GridToolbar,
    GridActionsCellItem,
    GridRowSelectionModel,
} from "@mui/x-data-grid";
import { Paper, Tooltip } from "@mui/material";
import { Edit, Delete, Visibility } from "@mui/icons-material";

interface DataTableProps {
    rows: GridRowsProp;
    columns: GridColDef[];
    loading?: boolean;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string) => void;
    hideActions?: boolean;
    pageSize?: number;
    checkboxSelection?: boolean;
    disableSelectionOnClick?: boolean;
}

interface ServerSideDataTableProps<T> {
    rows: T[];
    columns: GridColDef[];
    loading?: boolean;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string) => void;
    hideActions?: boolean;
    pageSize?: number;
    checkboxSelection?: boolean;
    disableSelectionOnClick?: boolean;
    totalRows?: number;
    onPaginationModelChange?: (model: {
        page: number;
        pageSize: number;
    }) => void;
    paginationModel?: { page: number; pageSize: number };
    getRowId?: (row: T) => string;
    rowSelectionModel?: GridRowSelectionModel;
    onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
    /**
     * Draw every column at full width, for a page that puts the table in a
     * scrolling box of its own (Orders). Otherwise the table fits its space
     * and scrolls sideways inside itself.
     */
    fullWidthColumns?: boolean;
}

export const DataTable: React.FC<DataTableProps> = ({
    rows,
    columns,
    loading = false,
    onEdit,
    onDelete,
    onView,
    hideActions = false,
    pageSize = 50,
    checkboxSelection = false,
    disableSelectionOnClick = true,
}) => {
    const actionsColumn: GridColDef = {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 120,
        getActions: (params) => {
            const actions = [];

            if (onView) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="View">
                                <Visibility />
                            </Tooltip>
                        }
                        label="View"
                        onClick={() => onView(params.id as string)}
                    />,
                );
            }

            if (onEdit) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Edit">
                                <Edit />
                            </Tooltip>
                        }
                        label="Edit"
                        onClick={() => onEdit(params.id as string)}
                    />,
                );
            }

            if (onDelete) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Delete">
                                <Delete />
                            </Tooltip>
                        }
                        label="Delete"
                        onClick={() => onDelete(params.id as string)}
                    />,
                );
            }

            return actions;
        },
    };

    return (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <DataGrid
                rows={rows}
                columns={hideActions ? columns : [...columns, actionsColumn]}
                loading={loading}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize },
                    },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick={disableSelectionOnClick}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                    },
                }}
                sx={{
                    "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "action.hover",
                    },
                    // Mobile responsive
                    "& .MuiDataGrid-columnHeaders": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                    "& .MuiDataGrid-cell": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        padding: { xs: "4px", sm: "8px" },
                    },
                }}
            />
        </Paper>
    );
};

const ServerSideDataTableComponent: React.FC<ServerSideDataTableProps<any>> = ({
    rows,
    columns,
    loading = false,
    onEdit,
    onDelete,
    onView,
    hideActions = false,
    pageSize = 50,
    checkboxSelection = false,
    disableSelectionOnClick = true,
    totalRows = 0,
    onPaginationModelChange,
    paginationModel = { page: 0, pageSize },
    getRowId,
    rowSelectionModel,
    onRowSelectionModelChange,
    fullWidthColumns = false,
}) => {
    // Built once per columns/handlers change — a new array on every render
    // makes the grid rebuild every cell, even when nothing in it changed.
    const finalColumns = React.useMemo(() => {
        const actionsColumn: GridColDef = {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 120,
            getActions: (params) => {
                const actions = [];

                if (onView) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="View">
                                    <Visibility />
                                </Tooltip>
                            }
                            label="View"
                            onClick={() => onView(params.id as string)}
                        />,
                    );
                }

                if (onEdit) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Edit">
                                    <Edit />
                                </Tooltip>
                            }
                            label="Edit"
                            onClick={() => onEdit(params.id as string)}
                        />,
                    );
                }

                if (onDelete) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Delete">
                                    <Delete />
                                </Tooltip>
                            }
                            label="Delete"
                            onClick={() => onDelete(params.id as string)}
                        />,
                    );
                }

                return actions;
            },
        };

        return hideActions ? columns : [...columns, actionsColumn];
    }, [columns, hideActions, onView, onEdit, onDelete]);

    return (
        <Paper
            sx={{
                height: "fit-content",
                width: "100%",
                // The table scrolls inside its own box when its columns don't
                // fit — the page itself never gets wider than the screen, so
                // the page header and buttons stay in view. The box is at
                // most one screen tall (below the app bar), so its sideways
                // scrollbar and column headers are always on screen; the
                // grid grows with its rows up to that height.
                ...(fullWidthColumns
                    ? { minWidth: "max-content", "& .MuiDataGrid-root": { minWidth: "max-content" } }
                    : {
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          maxHeight: "calc(100dvh - 96px)",
                      }),
            }}
        >
            <DataGrid
                rows={rows}
                columns={finalColumns}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                pageSizeOptions={[10, 25, 50, 100]}
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick={disableSelectionOnClick}
                getRowId={getRowId}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                slots={{
                    toolbar: GridToolbar,
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                    },
                }}
                sx={{
                    border: 0,
                    "& .MuiDataGrid-cell:hover": {
                        color: "primary.main",
                    },
                    ...(fullWidthColumns && {
                        "& .MuiDataGrid-main": { overflowX: "visible" },
                        "& .MuiDataGrid-virtualScroller": { overflowX: "visible !important" },
                    }),
                }}
            />
        </Paper>
    );
};

// Skips re-rendering when the page re-renders for reasons unrelated to the
// table (e.g. a search box or dialog), as long as its props are stable.
export const ServerSideDataTable = React.memo(ServerSideDataTableComponent);
